import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import {
  FloatLabelType,
  MatFormFieldModule,
} from '@angular/material/form-field';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Subject, map, takeUntil } from 'rxjs';

import { ApiTesterService } from '@core/services/api-tester.service';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { toSignal } from '@angular/core/rxjs-interop';

@Component({
    selector: 'rh-api-tester-controls',
    imports: [
        CommonModule,
        MatButtonModule,
        MatCheckboxModule,
        MatFormFieldModule,
        MatGridListModule,
        MatInputModule,
        MatSelectModule,
        ReactiveFormsModule,
    ],
    templateUrl: './api-tester-controls.component.html',
    styleUrl: './api-tester-controls.component.scss'
})
export class ApiTesterControlsComponent implements OnInit, OnDestroy {
  readonly apiTesterService = inject(ApiTesterService);

  readonly httpMethod = new FormControl(this.apiTesterService.httpMethod());
  readonly uri = new FormControl(this.apiTesterService.uri());
  readonly httpBody = new FormControl(this.apiTesterService.httpBody());

  readonly hideRequiredControl = new FormControl(false);
  readonly floatLabelControl = new FormControl('auto' as FloatLabelType);
  private readonly onDestroy = new Subject();

  ngOnInit() {
    this.httpMethod.valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe((v) =>
        this.apiTesterService.setHttpMethod(
          v as 'POST' | 'GET' | 'PATCH' | 'PUT'
        )
      );
    this.uri.valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe((v) => this.apiTesterService.setUri(v as string));
    this.httpBody.valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe((v) => this.apiTesterService.setHttpBody(v as string));
  }

  ngOnDestroy(): void {
    this.onDestroy.next(null);
    this.onDestroy.complete();
    this.apiTesterService.clearResponse();
  }

  public isValid() {
    return this.uri.valid && this.httpMethod.valid && this.httpBody.valid;
  }

  public runTest() {
    if (this.isValid()) {
      this.apiTesterService.runTest();
    }
  }

  protected readonly hideRequired = toSignal(
    this.hideRequiredControl.valueChanges
  );

  protected readonly floatLabel = toSignal(
    this.floatLabelControl.valueChanges.pipe(
      takeUntil(this.onDestroy),
      map((v) => v ?? 'auto')
    ),
    { initialValue: 'auto' }
  );
}
